package com.merkost.handpass.admin.container

import com.merkost.handpass.admin.api.AdminRepositoryApi
import com.merkost.handpass.admin.api.uploadFileToPresignedUrl
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.PartnerListingsAction
import com.merkost.handpass.admin.contract.PartnerListingsIntent
import com.merkost.handpass.admin.contract.PartnerListingsState
import com.merkost.handpass.admin.util.toAdminMessage
import com.merkost.handpass.api.dto.MonetaryAmount
import com.merkost.handpass.api.dto.admin.AdminCreateListingRequest
import com.merkost.handpass.api.dto.listing.CreateListingRequest
import com.merkost.handpass.api.dto.listing.ListingLocationDto
import com.merkost.handpass.api.enums.Condition
import com.varabyte.kobweb.core.AppGlobals
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.launch
import pro.respawn.flowmvi.api.Container
import pro.respawn.flowmvi.api.PipelineContext
import pro.respawn.flowmvi.dsl.lazyStore
import pro.respawn.flowmvi.plugins.recover
import pro.respawn.flowmvi.plugins.reduce

private val SUPABASE_URL: String = AppGlobals.getValue("admin.supabaseUrl")

@OptIn(FlowPreview::class)
class PartnerListingsContainer(
    private val repo: AdminRepositoryApi,
) : Container<PartnerListingsState, PartnerListingsIntent, PartnerListingsAction> {

    private val scope = MainScope()
    private val sellerSearchFlow = MutableSharedFlow<String>(replay = 1)

    override val store by lazyStore(PartnerListingsState(), scope) {
        configure { name = "PartnerListingsStore" }
        recover { e ->
            updateState {
                copy(
                    categories = AsyncSection.Failed(e.toAdminMessage()),
                    sellerSearchLoading = false,
                    isUploadingImage = false,
                    isSubmitting = false,
                )
            }
            null
        }
        reduce { intent ->
            when (intent) {
                PartnerListingsIntent.LoadCategories -> {
                    val categories = repo.getCategoryTree()
                    updateState {
                        copy(
                            categories = categories?.let { AsyncSection.Loaded(it) }
                                ?: AsyncSection.Failed("Could not load categories"),
                        )
                    }
                }
                is PartnerListingsIntent.SetSellerQuery -> {
                    updateState {
                        copy(
                            sellerQuery = intent.value,
                            sellerSearchLoading = intent.value.isNotBlank(),
                        )
                    }
                    sellerSearchFlow.tryEmit(intent.value)
                }
                is PartnerListingsIntent.SearchSellers -> {
                    val query = intent.value.trim()
                    if (query.isBlank()) {
                        updateState { copy(sellerResults = emptyList(), sellerSearchLoading = false) }
                    } else {
                        val result = runCatching { repo.getUsers(pageSize = 8, search = query) }
                        result.fold(
                            onSuccess = { response ->
                                updateState {
                                    copy(sellerResults = response.data, sellerSearchLoading = false)
                                }
                            },
                            onFailure = { error ->
                                updateState { copy(sellerResults = emptyList(), sellerSearchLoading = false) }
                                action(PartnerListingsAction.ShowError(error.toAdminMessage()))
                            },
                        )
                    }
                }
                is PartnerListingsIntent.SelectSeller -> {
                    updateState {
                        copy(
                            selectedSeller = intent.seller,
                            sellerQuery = "",
                            sellerResults = emptyList(),
                            sellerSearchLoading = false,
                            uploadedImageUrls = emptyList(),
                        )
                    }
                }
                is PartnerListingsIntent.UploadImage -> uploadImage(intent.file)
                is PartnerListingsIntent.RemoveImage -> {
                    updateState { copy(uploadedImageUrls = uploadedImageUrls - intent.url) }
                }
                is PartnerListingsIntent.Submit -> submitListing(intent)
            }
        }
    }

    init {
        scope.launch { store.intent(PartnerListingsIntent.LoadCategories) }
        scope.launch {
            sellerSearchFlow.debounce(300).distinctUntilChanged().collect {
                store.intent(PartnerListingsIntent.SearchSellers(it))
            }
        }
    }

    private suspend fun PipelineContext<PartnerListingsState, PartnerListingsIntent, PartnerListingsAction>.currentState(): PartnerListingsState {
        var state = PartnerListingsState()
        withState { state = this }
        return state
    }

    private suspend fun PipelineContext<PartnerListingsState, PartnerListingsIntent, PartnerListingsAction>.uploadImage(file: org.w3c.files.File) {
        val seller = currentState().selectedSeller
        if (seller == null) {
            action(PartnerListingsAction.ShowError("Select a seller before uploading images"))
            return
        }

        updateState { copy(isUploadingImage = true) }
        try {
            val contentType = file.type.ifBlank { "application/octet-stream" }
            val upload = repo.getSellerUploadUrl(seller.id, contentType, file.size.toLong())
                ?: error("Could not prepare image upload")
            if (!uploadFileToPresignedUrl(upload.uploadUrl, file, contentType)) {
                error("Image upload failed")
            }
            val publicUrl = "$SUPABASE_URL/storage/v1/object/public/listing-images/${upload.storageKey}"
            updateState { copy(uploadedImageUrls = uploadedImageUrls + publicUrl) }
        } catch (error: Throwable) {
            action(PartnerListingsAction.ShowError(error.toAdminMessage()))
        } finally {
            updateState { copy(isUploadingImage = false) }
        }
    }

    private suspend fun PipelineContext<PartnerListingsState, PartnerListingsIntent, PartnerListingsAction>.submitListing(
        intent: PartnerListingsIntent.Submit,
    ) {
        val state = currentState()
        val seller = state.selectedSeller
        if (seller == null) {
            action(PartnerListingsAction.ShowError("Select a seller before creating a listing"))
            return
        }

        val priceMinorUnits = kotlin.math.round((intent.priceDollars.toDoubleOrNull() ?: 0.0) * 100).toLong()
        val locationDto = ListingLocationDto(
            city = intent.city.ifBlank { null },
            latitude = intent.latitude.toDoubleOrNull(),
            longitude = intent.longitude.toDoubleOrNull(),
        )
        val request = AdminCreateListingRequest(
            sellerId = seller.id,
            listing = CreateListingRequest(
                categoryId = intent.categoryId,
                title = intent.title,
                description = intent.description.ifBlank { null },
                price = MonetaryAmount(priceMinorUnits, "AUD"),
                condition = intent.condition,
                images = state.uploadedImageUrls,
                location = locationDto,
            ),
        )

        updateState { copy(isSubmitting = true) }
        val result = repo.createPartnerListing(request)
        result.fold(
            onSuccess = {
                action(PartnerListingsAction.ShowSuccess("Partner listing created"))
                updateState {
                    copy(
                        selectedSeller = null,
                        sellerQuery = "",
                        sellerResults = emptyList(),
                        uploadedImageUrls = emptyList(),
                        isSubmitting = false,
                    )
                }
            },
            onFailure = { error ->
                updateState { copy(isSubmitting = false) }
                action(PartnerListingsAction.ShowError(error.toAdminMessage()))
            },
        )
    }
}
